import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PenTool } from 'lucide-react';

export const PromptingSection = () => {
  return (
    <Card className="bg-gray-800/20 border-gray-700 text-white">
      <CardHeader>
        <CardTitle className="text-2xl">Effektives Prompting</CardTitle>
        <CardDescription className="text-gray-400">
          Wie man die besten Ergebnisse aus der KI herausholt
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="text-xl font-medium mb-3">Grundlagen des Promptings</h3>
          <p className="text-gray-300 leading-relaxed">
            Ein guter Prompt ist der Schlüssel zu hervorragenden KI-Ergebnissen. Je präziser und detaillierter Ihre Anfrage ist, 
            desto besser kann die KI auf Ihre Bedürfnisse eingehen.
          </p>
        </div>

        <div>
          <h3 className="text-xl font-medium mb-3">Tipps für effektives Prompting</h3>
          <ul className="space-y-3 text-gray-300">
            <li className="flex items-start">
              <div className="bg-jade/10 p-1 rounded-full mt-1 mr-3">
                <PenTool className="h-4 w-4 text-jade" />
              </div>
              <div>
                <span className="font-medium text-white">Seien Sie spezifisch</span>
                <p className="text-sm text-gray-400 mt-1">Statt "Erstelle eine E-Mail" versuchen Sie "Erstelle eine E-Mail für einen Neukunden zur Vorstellung einer Unfallversicherung mit besonderen Vorteilen für Sportler".</p>
              </div>
            </li>
            <li className="flex items-start">
              <div className="bg-jade/10 p-1 rounded-full mt-1 mr-3">
                <PenTool className="h-4 w-4 text-jade" />
              </div>
              <div>
                <span className="font-medium text-white">Definieren Sie den Zweck</span>
                <p className="text-sm text-gray-400 mt-1">Geben Sie an, welches Ziel die E-Mail erreichen soll, z.B. "...mit dem Ziel, einen Beratungstermin zu vereinbaren".</p>
              </div>
            </li>
            <li className="flex items-start">
              <div className="bg-jade/10 p-1 rounded-full mt-1 mr-3">
                <PenTool className="h-4 w-4 text-jade" />
              </div>
              <div>
                <span className="font-medium text-white">Beschreiben Sie den Ton</span>
                <p className="text-sm text-gray-400 mt-1">Geben Sie an, welchen Tonfall die E-Mail haben soll, z.B. "...in einem professionellen aber freundlichen Ton".</p>
              </div>
            </li>
            <li className="flex items-start">
              <div className="bg-jade/10 p-1 rounded-full mt-1 mr-3">
                <PenTool className="h-4 w-4 text-jade" />
              </div>
              <div>
                <span className="font-medium text-white">Nennen Sie wichtige Details</span>
                <p className="text-sm text-gray-400 mt-1">Fügen Sie relevante Informationen hinzu, die in der E-Mail erscheinen sollen, z.B. "...erwähne besonders die Deckung von Extremsportarten und die schnelle Schadenregulierung".</p>
              </div>
            </li>
          </ul>
        </div>

        <div>
          <h3 className="text-xl font-medium mb-3">Beispiel-Prompts</h3>
          <div className="space-y-4">
            <div className="bg-gray-700/50 p-4 rounded-md">
              <h4 className="text-jade font-medium mb-2">Für E-Mail-Vorlagen</h4>
              <p className="text-white text-sm italic">"Erstelle ein HTML E-Mail-Template für eine Willkommens-E-Mail an Neukunden, die gerade eine Hausratversicherung abgeschlossen haben. Die E-Mail sollte die nächsten Schritte erklären, wie sie ihre Policendokumente erhalten, und Kontaktmöglichkeiten bei Fragen anbieten. Der Ton sollte herzlich aber professionell sein."</p>
            </div>

            <div className="bg-gray-700/50 p-4 rounded-md">
              <h4 className="text-jade font-medium mb-2">Für Versicherungsvergleiche</h4>
              <p className="text-white text-sm italic">"Erstelle einen Vergleich zwischen drei KFZ-Versicherungstarifen mit Fokus auf die Unterschiede in der Deckung bei Kaskoschäden, Selbstbeteiligung und besonderen Leistungen wie Mallorca-Police oder GAP-Deckung. Formatiere den Vergleich in einer übersichtlichen Tabelle und hebe die besten Optionen je nach Kundenbedürfnis hervor."</p>
            </div>

            <div className="bg-gray-700/50 p-4 rounded-md">
              <h4 className="text-jade font-medium mb-2">Für Angebotsnachfassen</h4>
              <p className="text-white text-sm italic">"Erstelle eine Nachfass-E-Mail für einen Kunden, dem vor zwei Wochen ein Angebot für eine Berufsunfähigkeitsversicherung zugesandt wurde. Die E-Mail sollte freundlich sein, die Vorteile des Angebots nochmals kurz zusammenfassen und eine klare Handlungsaufforderung enthalten."</p>
            </div>
          </div>
        </div>

        <div>
          <h3 className="text-xl font-medium mb-3">Mehrsprachigkeit</h3>
          <p className="text-gray-300 leading-relaxed mb-4">
            Unsere KI unterstützt mehrere Sprachen. Sie können Prompts in verschiedenen Sprachen eingeben und erhalten 
            Antworten in derselben Sprache. Standardmäßig ist die Sprache auf Deutsch eingestellt, aber Sie können 
            in jeder unterstützten Sprache arbeiten.
          </p>
          <div className="bg-gray-700/50 p-4 rounded-md">
            <h4 className="text-jade font-medium mb-2">Beispiel für englischsprachigen Prompt</h4>
            <p className="text-white text-sm italic">"Create an email template for introducing a new health insurance product to existing customers, highlighting its unique benefits for families with children. The tone should be professional yet caring."</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
